'use client';
/**
 * HeroLogisticsAnimation  v4
 * ──────────────────────────
 * Desktop: position:absolute, inset:0 — trucks + city nodes + minimal phone card as background layer
 * Mobile:  same absolute fill, but CSS makes only bottom strip visible (trucks + compact phone)
 */
import { useEffect, useRef } from 'react';

const CSS = `
@keyframes hl-ltr {
  0%   { transform:translateX(-260px); opacity:0; }
  2%   { opacity:1; }
  15%  { opacity:0.85; }
  96%  { opacity:0.85; }
  100% { transform:translateX(calc(100vw + 260px)); opacity:0; }
}
@keyframes hl-rtl {
  0%   { transform:translateX(calc(100vw + 260px)); opacity:0; }
  2%   { opacity:1; }
  15%  { opacity:0.85; }
  96%  { opacity:0.85; }
  100% { transform:translateX(-260px); opacity:0; }
}
@keyframes hl-ping {
  0%   { transform:translate(-50%,-50%) scale(1);   opacity:0.5; }
  100% { transform:translate(-50%,-50%) scale(3.5); opacity:0;   }
}
@keyframes hl-glow-pulse {
  0%,100% { box-shadow: 0 0 18px rgba(0,191,165,0.25), 0 0 0 1px rgba(0,191,165,0.4); }
  50%     { box-shadow: 0 0 32px rgba(0,191,165,0.45), 0 0 0 1px rgba(0,191,165,0.6); }
}
@keyframes hl-ring-pulse {
  0%   { transform:translate(-50%,-50%) scale(1);   opacity:0.5; }
  100% { transform:translate(-50%,-50%) scale(1.55); opacity:0;  }
}

/* ── Mobile overrides ── */
@media (max-width: 899px) {
  /* Hide vehicles, parcels, phone — keep orbit rings only */
  .hl-parcel        { display:none !important; }
  .hl-lane-dt       { display:none !important; }
  .hl-mobile-net    { display:none !important; }
  .hl-vehicles-wrap { display:none !important; }
  .hl-phone-wrap    { display:none !important; }
  /* Orbit nodes — visible but pushed right, labels hidden */
  .hl-node          { opacity: 0.28; }
  .hl-node-label    { display:none !important; }
}

/* Vehicles wrapper — adds left-side dimming mask */
.hl-vehicles-wrap {
  z-index: 10;
  position: relative;
  -webkit-mask-image: linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 15%, rgba(0,0,0,1) 35%, rgba(0,0,0,1) 100%);
  mask-image: linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 15%, rgba(0,0,0,1) 35%, rgba(0,0,0,1) 100%);
}

/* Orbits/coordinate rings — appear behind vehicles */
.hl-node::before,
.hl-node::after {
  z-index: 1 !important;
}
`;

// ─── SVG Trucks ────────────────────────────────────────────────────────────────

function SemiTruck({ p = '#263238', a = '#00BFA5', uid = 's' }) {
  return (
    <svg width="128" height="42" viewBox="0 0 128 42" fill="none">
      <defs>
        <linearGradient id={`sb${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#455A64" />
          <stop offset="100%" stopColor={p} />
        </linearGradient>
        <linearGradient id={`sc${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#546E7A" />
          <stop offset="100%" stopColor={p} />
        </linearGradient>
        <linearGradient id={`sg${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#B2EBF2" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#80DEEA" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <rect x="1" y="7" width="77" height="25" rx="2" fill={`url(#sb${uid})`} />
      <rect x="1" y="7" width="77" height="3" rx="1" fill="rgba(255,255,255,0.1)" />
      {[13, 25, 37, 49, 61, 73].map((x) => (
        <line key={x} x1={x} y1="7" x2={x} y2="32" stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
      ))}
      <rect x="4" y="14" width="68" height="5" rx="1" fill={a} opacity="0.18" />
      <rect x="1" y="8" width="7" height="23" rx="1" fill="rgba(0,0,0,0.22)" />
      <rect x="1" y="20" width="2.5" height="5" rx="0.5" fill="#ef5350" opacity="0.85" />
      <rect x="78" y="21" width="7" height="5" rx="1" fill="rgba(0,0,0,0.35)" />
      <rect x="85" y="5" width="41" height="27" rx="4" fill={`url(#sc${uid})`} />
      <rect x="88" y="3" width="33" height="5" rx="2" fill="#37474F" />
      <rect x="109" y="7" width="14" height="13" rx="2" fill={`url(#sg${uid})`} />
      <line x1="116" y1="7" x2="116" y2="20" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
      <rect x="88" y="8" width="19" height="10" rx="1.5" fill={`url(#sg${uid})`} opacity="0.75" />
      <rect x="91" y="0" width="3" height="7" rx="1" fill="#1C313A" />
      <rect x="124" y="24" width="4" height="6" rx="1" fill="#1C313A" />
      <rect x="124" y="17" width="4" height="5" rx="1" fill="#FFF9C4" opacity="0.9" />
      <rect x="85" y="27" width="39" height="2" rx="1" fill={a} opacity="0.4" />
      {[12, 28, 55, 67, 93, 108].map((cx) => (
        <g key={cx}>
          <circle cx={cx} cy="35" r="6" fill="#102027" />
          <circle cx={cx} cy="35" r="3.5" fill="#1C313A" />
          <circle cx={cx} cy="35" r="1.5" fill="#263238" />
        </g>
      ))}
    </svg>
  );
}

function BoxTruck({ p = '#1A237E', a = '#00BFA5', uid = 'b' }) {
  return (
    <svg width="82" height="38" viewBox="0 0 82 38" fill="none">
      <defs>
        <linearGradient id={`bbd${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#283593" />
          <stop offset="100%" stopColor={p} />
        </linearGradient>
        <linearGradient id={`bbc${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#303F9F" />
          <stop offset="100%" stopColor={p} />
        </linearGradient>
        <linearGradient id={`bbg${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#B3E5FC" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#81D4FA" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <rect x="1" y="5" width="55" height="24" rx="2" fill={`url(#bbd${uid})`} />
      <rect x="1" y="5" width="55" height="3.5" rx="1" fill="rgba(255,255,255,0.1)" />
      {[14, 28, 42].map((x) => (
        <line key={x} x1={x} y1="5" x2={x} y2="29" stroke="rgba(0,0,0,0.2)" strokeWidth="0.8" />
      ))}
      <rect x="4" y="11" width="47" height="5" rx="1" fill={a} opacity="0.2" />
      <rect x="1" y="6" width="6" height="22" rx="1" fill="rgba(0,0,0,0.2)" />
      <rect x="1" y="18" width="2" height="5" rx="0.5" fill="#ef5350" opacity="0.85" />
      <rect x="56" y="17" width="5" height="4" rx="1" fill="rgba(0,0,0,0.3)" />
      <path
        d="M61,7 L61,29 Q61,31 63,31 L79,31 Q81,31 81,29 L81,17 Q81,13 77,11 L67,8 Z"
        fill={`url(#bbc${uid})`}
      />
      <path d="M67,9 L75,9 Q79,9 79,14 L79,20 L67,20 Z" fill={`url(#bbg${uid})`} />
      <rect x="62" y="11" width="4" height="9" rx="1" fill={`url(#bbg${uid})`} opacity="0.7" />
      <rect x="79" y="19" width="3" height="5" rx="0.5" fill="#FFF9C4" opacity="0.9" />
      <rect x="79" y="25" width="3" height="4" rx="1" fill="#102027" />
      <rect x="61" y="27" width="20" height="1.5" rx="0.5" fill={a} opacity="0.45" />
      {[11, 40, 65].map((cx) => (
        <g key={cx}>
          <circle cx={cx} cy="32" r="5.5" fill="#102027" />
          <circle cx={cx} cy="32" r="3.2" fill="#1C313A" />
          <circle cx={cx} cy="32" r="1.5" fill="#263238" />
        </g>
      ))}
    </svg>
  );
}

function DeliveryVan({ p = '#1B5E20', a = '#00BFA5', uid = 'd' }) {
  return (
    <svg width="64" height="34" viewBox="0 0 64 34" fill="none">
      <defs>
        <linearGradient id={`dvb${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2E7D32" />
          <stop offset="100%" stopColor={p} />
        </linearGradient>
        <linearGradient id={`dvg${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C8E6C9" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#A5D6A7" stopOpacity="0.65" />
        </linearGradient>
      </defs>
      <path
        d="M2,27 L2,11 Q2,5 8,5 L42,5 Q49,5 53,10 L62,21 Q64,24 62,27 L2,27 Z"
        fill={`url(#dvb${uid})`}
      />
      <path d="M8,5 L42,5 Q47,5 51,9 L8,9 Z" fill="rgba(255,255,255,0.1)" />
      <line x1="35" y1="5" x2="35" y2="27" stroke="rgba(0,0,0,0.18)" strokeWidth="1.5" />
      <path d="M42,6 L51,9 L60,21 L42,21 Z" fill={`url(#dvg${uid})`} />
      <rect x="37" y="7" width="4" height="9" rx="1" fill={`url(#dvg${uid})`} opacity="0.7" />
      <rect x="8" y="9" width="24" height="14" rx="1" fill="rgba(0,0,0,0.08)" />
      <line x1="20" y1="9" x2="20" y2="23" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8" />
      <rect x="4" y="19" width="24" height="2" rx="1" fill={a} opacity="0.35" />
      <rect x="1" y="19" width="2.5" height="6" rx="0.5" fill="#ef5350" opacity="0.8" />
      <circle cx="61" cy="21" r="1.8" fill="#FFF9C4" opacity="0.9" />
      <rect x="2" y="25" width="60" height="1.5" rx="0.5" fill={a} opacity="0.3" />
      <g>
        <circle cx="13" cy="29" r="5" fill="#102027" />
        <circle cx="13" cy="29" r="3" fill="#1C313A" />
        <circle cx="13" cy="29" r="1.4" fill="#263238" />
      </g>
      <g>
        <circle cx="52" cy="29" r="5" fill="#102027" />
        <circle cx="52" cy="29" r="3" fill="#1C313A" />
        <circle cx="52" cy="29" r="1.4" fill="#263238" />
      </g>
    </svg>
  );
}

function FlatbedTruck({ p = '#37474F', a = '#00BFA5', uid = 'f' }) {
  return (
    <svg width="94" height="40" viewBox="0 0 94 40" fill="none">
      <defs>
        <linearGradient id={`fbc${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#546E7A" />
          <stop offset="100%" stopColor={p} />
        </linearGradient>
        <linearGradient id={`fbg${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#B2EBF2" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#80DEEA" stopOpacity="0.65" />
        </linearGradient>
        <linearGradient id={`fbk${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#607D8B" />
          <stop offset="100%" stopColor="#455A64" />
        </linearGradient>
      </defs>
      <rect x="1" y="22" width="68" height="5" rx="1" fill={p} />
      <rect x="4" y="10" width="13" height="13" rx="1" fill={`url(#fbk${uid})`} />
      <line x1="10" y1="10" x2="10" y2="23" stroke="rgba(0,0,0,0.2)" strokeWidth="0.8" />
      <rect x="19" y="7" width="17" height="16" rx="1" fill={`url(#fbk${uid})`} opacity="0.9" />
      <line x1="27" y1="7" x2="27" y2="23" stroke="rgba(0,0,0,0.2)" strokeWidth="0.8" />
      <rect x="38" y="9" width="13" height="14" rx="1" fill={`url(#fbk${uid})`} opacity="0.85" />
      {[9, 27, 45].map((x) => (
        <line
          key={x}
          x1={x}
          y1="8"
          x2={x}
          y2="27"
          stroke="#FBC02D"
          strokeWidth="1.2"
          opacity="0.7"
        />
      ))}
      <rect x="62" y="12" width="5" height="15" rx="1" fill={p} />
      <rect x="68" y="23" width="5" height="4" rx="1" fill="rgba(0,0,0,0.3)" />
      <rect x="73" y="6" width="20" height="24" rx="4" fill={`url(#fbc${uid})`} />
      <rect x="85" y="8" width="7" height="11" rx="1.5" fill={`url(#fbg${uid})`} />
      <rect x="75" y="9" width="9" height="9" rx="1" fill={`url(#fbg${uid})`} opacity="0.7" />
      <rect x="91" y="18" width="3" height="5" rx="0.5" fill="#FFF9C4" opacity="0.9" />
      <rect x="73" y="27" width="20" height="1.5" rx="0.5" fill={a} opacity="0.4" />
      {[10, 51, 70, 82].map((cx) => (
        <g key={cx}>
          <circle cx={cx} cy="33" r="6" fill="#102027" />
          <circle cx={cx} cy="33" r="3.5" fill="#1C313A" />
          <circle cx={cx} cy="33" r="1.6" fill="#263238" />
        </g>
      ))}
    </svg>
  );
}

function PickupTruck({ p = '#004D40', a = '#00BFA5', uid = 'k' }) {
  return (
    <svg width="56" height="30" viewBox="0 0 56 30" fill="none">
      <defs>
        <linearGradient id={`pkb${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00695C" />
          <stop offset="100%" stopColor={p} />
        </linearGradient>
        <linearGradient id={`pkg${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#B2EBF2" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#80DEEA" stopOpacity="0.65" />
        </linearGradient>
      </defs>
      <rect x="1" y="10" width="21" height="14" rx="1" fill={p} opacity="0.85" />
      <rect x="4" y="8" width="7" height="7" rx="1" fill="#455A64" opacity="0.7" />
      <rect x="13" y="9" width="5" height="6" rx="1" fill="#455A64" opacity="0.6" />
      <path d="M22,5 L22,24 L54,24 L54,14 Q54,10 50,8 L34,5 Z" fill={`url(#pkb${uid})`} />
      <path d="M40,5.5 L50,8 L52,14 L40,14 Z" fill={`url(#pkg${uid})`} />
      <rect x="24" y="7" width="13" height="8" rx="1" fill={`url(#pkg${uid})`} opacity="0.7" />
      <line x1="30" y1="7" x2="30" y2="15" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8" />
      <rect x="1" y="15" width="1.5" height="4" rx="0.4" fill="#ef5350" opacity="0.8" />
      <circle cx="53" cy="17" r="1.5" fill="#FFF9C4" opacity="0.9" />
      <rect x="22" y="22" width="32" height="1.2" rx="0.4" fill={a} opacity="0.4" />
      <g>
        <circle cx="10" cy="26" r="4" fill="#102027" />
        <circle cx="10" cy="26" r="2.3" fill="#1C313A" />
        <circle cx="10" cy="26" r="1" fill="#263238" />
      </g>
      <g>
        <circle cx="44" cy="26" r="4" fill="#102027" />
        <circle cx="44" cy="26" r="2.3" fill="#1C313A" />
        <circle cx="44" cy="26" r="1" fill="#263238" />
      </g>
    </svg>
  );
}

function Mirrored({ children }) {
  return <div style={{ display: 'inline-block', transform: 'scaleX(-1)' }}>{children}</div>;
}

// ─── Lanes ─────────────────────────────────────────────────────────────────────
const LANES = [
  // ── always visible (desktop + mobile) ──
  {
    top: '12%',
    dir: 'ltr',
    dur: 18,
    del: 0,
    V: SemiTruck,
    p: '#263238',
    a: '#00BFA5',
    uid: 's0',
    dt: false,
  },
  {
    top: '32%',
    dir: 'rtl',
    dur: 22,
    del: 7,
    V: DeliveryVan,
    p: '#1B5E20',
    a: '#00BFA5',
    uid: 'd0',
    dt: false,
  },
  {
    top: '52%',
    dir: 'ltr',
    dur: 20,
    del: 3,
    V: FlatbedTruck,
    p: '#263238',
    a: '#00BFA5',
    uid: 'f0',
    dt: false,
  },
  {
    top: '72%',
    dir: 'rtl',
    dur: 16,
    del: 11,
    V: PickupTruck,
    p: '#004D40',
    a: '#00BFA5',
    uid: 'k0',
    dt: false,
  },
  // ── desktop-only ──
  {
    top: '22%',
    dir: 'ltr',
    dur: 14,
    del: 5,
    V: BoxTruck,
    p: '#1565C0',
    a: '#4FC3F7',
    uid: 'b1',
    dt: true,
  },
  {
    top: '62%',
    dir: 'rtl',
    dur: 19,
    del: 2,
    V: SemiTruck,
    p: '#37474F',
    a: '#80CBC4',
    uid: 's3',
    dt: true,
  },
  {
    top: '85%',
    dir: 'ltr',
    dur: 22,
    del: 9,
    V: DeliveryVan,
    p: '#1B5E20',
    a: '#80CBC4',
    uid: 'd2',
    dt: true,
  },
  {
    top: '91%',
    dir: 'rtl',
    dur: 20,
    del: 14,
    V: BoxTruck,
    p: '#0D47A1',
    a: '#4FC3F7',
    uid: 'b2',
    dt: true,
  },
];

// ─── City nodes (right-half only, away from text) ──────────────────────────────
const NODES = [
  { label: 'Vizag', left: '91%', top: '7%', color: '#00BFA5', ping: true },
  { label: 'Hyderabad', left: '78%', top: '52%', color: '#4FC3F7', ping: true },
  { label: 'Vijayawada', left: '63%', top: '19%', color: '#80CBC4', ping: false },
  { label: 'Guntur', left: '72%', top: '79%', color: '#00BFA5', ping: false },
  { label: 'Srikakulam', left: '58%', top: '88%', color: '#4FC3F7', ping: false },
  { label: 'Vizianagaram', left: '85%', top: '33%', color: '#80CBC4', ping: false },
];

// ─── Parcel pins ───────────────────────────────────────────────────────────────
const PARCELS = [
  { left: '51%', top: '28%', n: 5, anim: 'hl-pfloat1', delay: '0s', dur: '3.8s' },
  { left: '59%', top: '61%', n: 3, anim: 'hl-pfloat2', delay: '1.1s', dur: '4.2s' },
  { left: '67%', top: '84%', n: 8, anim: 'hl-pfloat3', delay: '0.6s', dur: '3.4s' },
];

// Parcel keyframes injected via CSS string above — (hl-pfloat1/2/3)
function ParcelPin({ n }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <svg width="26" height="34" viewBox="0 0 26 34" fill="none">
        <path
          d="M13 0C7.5 0 3.5 4 3.5 9C3.5 16 13 27 13 27C13 27 22.5 16 22.5 9C22.5 4 18.5 0 13 0Z"
          fill="#00BFA5"
          opacity="0.88"
        />
        <circle cx="13" cy="9" r="5.5" fill="rgba(6,20,18,0.82)" />
        {/* tiny package icon */}
        <rect x="10" y="7" width="6" height="5" rx="0.5" fill="#00BFA5" />
        <line x1="10" y1="8.5" x2="16" y2="8.5" stroke="rgba(6,20,18,0.7)" strokeWidth="0.5" />
        <line x1="13" y1="7" x2="13" y2="12" stroke="rgba(6,20,18,0.7)" strokeWidth="0.5" />
        <ellipse cx="13" cy="28" rx="3.5" ry="1.2" fill="rgba(0,191,165,0.18)" />
      </svg>
      <div
        style={{
          background: 'rgba(0,191,165,0.12)',
          border: '1px solid rgba(0,191,165,0.38)',
          borderRadius: 10,
          padding: '2px 7px',
          fontSize: 9,
          fontWeight: 700,
          color: '#00BFA5',
          fontFamily: 'system-ui,sans-serif',
          letterSpacing: '0.07em',
        }}
      >
        {n} pkgs
      </div>
    </div>
  );
}

// ─── Simple Phone Card ─────────────────────────────────────────────────────────
// Minimal: just a screen with a route + CTA button. No command-center complexity.
function PhoneCard() {
  return (
    <svg
      width="74"
      height="128"
      viewBox="0 0 74 128"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="pc-frame" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1C3B36" />
          <stop offset="100%" stopColor="#0B2420" />
        </linearGradient>
        <linearGradient id="pc-btn" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#00BFA5" />
          <stop offset="100%" stopColor="#00897B" />
        </linearGradient>
      </defs>

      {/* Phone frame */}
      <rect
        x="2"
        y="2"
        width="70"
        height="124"
        rx="12"
        fill="url(#pc-frame)"
        stroke="rgba(0,191,165,0.55)"
        strokeWidth="1.2"
      />
      {/* Dynamic island */}
      <rect x="24" y="7" width="26" height="7" rx="3.5" fill="#061412" />
      {/* Screen */}
      <rect x="7" y="18" width="60" height="94" rx="4" fill="#070F0E" />
      {/* Home bar */}
      <rect x="26" y="121" width="22" height="2.5" rx="1.25" fill="rgba(0,191,165,0.4)" />

      {/* Header: "Book Pickup" */}
      <rect x="7" y="18" width="60" height="11" rx="4" fill="rgba(0,191,165,0.1)" />
      <rect x="16" y="22" width="28" height="2.5" rx="1" fill="rgba(255,255,255,0.45)" />
      <circle cx="60" cy="23.5" r="2.5" fill="rgba(0,191,165,0.5)" />

      {/* Map area */}
      <rect x="9" y="31" width="56" height="38" rx="3" fill="#050E0D" />
      {/* Map grid */}
      {[37, 43, 49, 55, 61].map((y) => (
        <line
          key={y}
          x1="9"
          y1={y}
          x2="65"
          y2={y}
          stroke="#00BFA5"
          strokeWidth="0.25"
          opacity="0.15"
        />
      ))}
      {[16, 24, 32, 40, 48, 56].map((x) => (
        <line
          key={x}
          x1={x}
          y1="31"
          x2={x}
          y2="69"
          stroke="#00BFA5"
          strokeWidth="0.25"
          opacity="0.15"
        />
      ))}
      {/* Route arc */}
      <path
        d="M15,63 Q30,42 59,36"
        fill="none"
        stroke="#00BFA5"
        strokeWidth="1.8"
        strokeDasharray="4 3"
        opacity="0.9"
      />
      {/* Origin dot */}
      <circle cx="15" cy="63" r="3.5" fill="#00BFA5" />
      <circle cx="15" cy="63" r="6" fill="none" stroke="#00BFA5" strokeWidth="0.7" opacity="0.35" />
      {/* Destination dot */}
      <circle cx="59" cy="36" r="3.5" fill="#4FC3F7" />
      <circle cx="59" cy="36" r="6" fill="none" stroke="#4FC3F7" strokeWidth="0.7" opacity="0.35" />
      {/* Moving dot */}
      <circle r="2.5" fill="#FFD54F" opacity="0.95">
        <animateMotion dur="3.4s" repeatCount="indefinite" path="M15,63 Q30,42 59,36" />
      </circle>

      {/* Divider */}
      <line x1="9" y1="72" x2="65" y2="72" stroke="rgba(0,191,165,0.12)" strokeWidth="0.5" />

      {/* Parcel summary row */}
      <rect
        x="10"
        y="75"
        width="12"
        height="12"
        rx="1.5"
        fill="none"
        stroke="#00BFA5"
        strokeWidth="1"
        opacity="0.7"
      />
      <line x1="10" y1="79" x2="22" y2="79" stroke="#00BFA5" strokeWidth="0.6" opacity="0.5" />
      <line x1="16" y1="75" x2="16" y2="87" stroke="#00BFA5" strokeWidth="0.6" opacity="0.5" />
      <rect x="26" y="76" width="26" height="2.5" rx="1" fill="rgba(255,255,255,0.5)" />
      <rect x="26" y="81" width="18" height="2" rx="1" fill="rgba(255,255,255,0.22)" />
      <rect x="26" y="85" width="22" height="2" rx="1" fill="rgba(0,191,165,0.3)" />

      {/* CTA button */}
      <rect x="10" y="93" width="54" height="14" rx="7" fill="url(#pc-btn)" />
      <rect x="16" y="98.5" width="30" height="2.5" rx="1" fill="rgba(255,255,255,0.9)" />
      <path
        d="M50,98 L55,101 L50,104"
        fill="none"
        stroke="rgba(255,255,255,0.7)"
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      {/* Live indicator */}
      <circle cx="14" cy="112" r="2.5" fill="#69F0AE" />
      <rect x="21" y="110.5" width="18" height="2" rx="1" fill="rgba(255,255,255,0.22)" />
    </svg>
  );
}

// ─── Main export ───────────────────────────────────────────────────────────────
export default function HeroLogisticsAnimation() {
  const styleRef = useRef(null);
  useEffect(() => {
    if (!styleRef.current) {
      const el = document.createElement('style');
      el.id = 'hl-v4-css';
      el.textContent = CSS;
      document.head.appendChild(el);
      styleRef.current = el;
    }
    return () => {
      styleRef.current?.remove();
      styleRef.current = null;
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {/* Grid mesh */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <pattern id="hl-grid" width="52" height="52" patternUnits="userSpaceOnUse">
            <path d="M52 0L0 0 0 52" fill="none" stroke="#00BFA5" strokeWidth="0.3" opacity="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hl-grid)" opacity="0.055" />
      </svg>

      {/* Lane glow lines */}
      <svg
        viewBox="0 0 1440 600"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <defs>
          <linearGradient id="hl-lg1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00BFA5" stopOpacity="0" />
            <stop offset="15%" stopColor="#00BFA5" stopOpacity="0.24" />
            <stop offset="85%" stopColor="#00BFA5" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#00BFA5" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="hl-lg2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4FC3F7" stopOpacity="0" />
            <stop offset="15%" stopColor="#4FC3F7" stopOpacity="0.15" />
            <stop offset="85%" stopColor="#4FC3F7" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#4FC3F7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[36, 118, 204, 292, 378, 462, 546].map((y, i) => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="1440"
            y2={y}
            stroke={i % 2 === 0 ? 'url(#hl-lg1)' : 'url(#hl-lg2)'}
            strokeWidth="1.1"
            strokeDasharray="22 16"
          >
            <animate
              attributeName="stroke-dashoffset"
              values={i % 2 === 0 ? '0;-38' : '0;38'}
              dur={`${2.5 + i * 0.45}s`}
              repeatCount="indefinite"
            />
          </line>
        ))}
        <path
          d="M0,580 Q360,370 720,290 T1440,70"
          fill="none"
          stroke="#00BFA5"
          strokeWidth="1"
          strokeDasharray="18 14"
          opacity="0.11"
        >
          <animate
            attributeName="stroke-dashoffset"
            values="0;-64"
            dur="6s"
            repeatCount="indefinite"
          />
        </path>
        <path
          d="M1440,560 Q1080,370 720,310 T0,90"
          fill="none"
          stroke="#4FC3F7"
          strokeWidth="1"
          strokeDasharray="18 14"
          opacity="0.08"
        >
          <animate
            attributeName="stroke-dashoffset"
            values="0;64"
            dur="7.5s"
            repeatCount="indefinite"
          />
        </path>
      </svg>

      {/* Vehicles */}
      <div className="hl-vehicles-wrap" style={{ position: 'absolute', inset: 0 }}>
        {LANES.map((lane) => {
          const isRtl = lane.dir === 'rtl';
          const V = lane.V;
          return (
            <div
              key={lane.uid}
              className={lane.dt ? 'hl-lane-dt' : undefined}
              style={{
                position: 'absolute',
                top: lane.top,
                left: 0,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                // On mobile, vehicles follow the lower network placement
                transform: 'translateY(var(--vh-offset, 0))',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  animation: `${isRtl ? 'hl-rtl' : 'hl-ltr'} ${lane.dur}s linear ${lane.del}s infinite`,
                  willChange: 'transform',
                  filter:
                    'drop-shadow(0 3px 12px rgba(0,191,165,0.2)) drop-shadow(0 1px 4px rgba(0,0,0,0.6))',
                }}
              >
                {isRtl ? (
                  <Mirrored>
                    <V p={lane.p} a={lane.a} uid={lane.uid} />
                  </Mirrored>
                ) : (
                  <V p={lane.p} a={lane.a} uid={lane.uid} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* City nodes */}
      {NODES.map((node, i) => (
        <div
          key={node.label}
          className="hl-node"
          style={{
            position: 'absolute',
            left: node.left,
            top: node.top,
            transform: 'translate(-50%,-50%)',
            zIndex: 5,
          }}
        >
          {node.ping &&
            [0, 0.65].map((offset, ri) => (
              <div
                key={ri}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 26 + ri * 14,
                  height: 26 + ri * 14,
                  borderRadius: '50%',
                  border: `1.3px solid ${node.color}`,
                  opacity: 0,
                  animation: `hl-ping 2.5s ease-out ${i * 0.55 + offset}s infinite`,
                }}
              />
            ))}
          <div
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: node.color,
              boxShadow: `0 0 0 3px ${node.color}20, 0 0 8px ${node.color}44`,
              position: 'relative',
              zIndex: 2,
            }}
          />
          <div
            className="hl-node-label"
            style={{
              position: 'absolute',
              top: 13,
              left: '50%',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              fontSize: 9.5,
              fontWeight: 700,
              color: 'rgba(0,60,48,0.6)',
              fontFamily: 'system-ui,sans-serif',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
            }}
          >
            {node.label}
          </div>
        </div>
      ))}

      {/* Parcel pickup pins */}
      {PARCELS.map((pp, i) => (
        <div
          key={i}
          className="hl-parcel"
          style={{
            position: 'absolute',
            left: pp.left,
            top: pp.top,
            transform: 'translate(-50%,-50%)',
            zIndex: 4,
            animation: `${pp.anim} ${pp.dur} ease-in-out ${pp.delay} infinite`,
          }}
        >
          <ParcelPin n={pp.n} />
        </div>
      ))}

      {/* ── Mobile city network — geographic map layout in bottom transparent zone ── */}
      {/*
          Rough AP geography mapped to 380×110 SVG space:
          Hyderabad  → far left, mid-low   (28, 68)
          Vijayawada → left-center, mid    (118, 50)
          Guntur     → left-center, low    (150, 80)
          Vizianagaram→right, upper        (272, 28)
          Vizag      → far right, top      (350, 18)
          preserveAspectRatio=none: SVG x→%width, SVG y→px height
      */}
      <div
        className="hl-mobile-net"
        style={{
          display: 'none',
          position: 'absolute',
          top: '88%',
          left: '2%',
          right: '2%',
          height: 120,
          zIndex: 7,
          pointerEvents: 'none',
        }}
      >
        <svg
          width="100%"
          height="130"
          viewBox="0 0 380 130"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '130px' }}
        >
          {/* NH-16 main coastal corridor: Hyd → Vjw → Vizianagaram → Vizag */}
          <line
            x1="28"
            y1="68"
            x2="118"
            y2="50"
            stroke="#80CBC4"
            strokeWidth="1.5"
            strokeDasharray="9 8"
            opacity="0.52"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="0;-17"
              dur="4.1s"
              repeatCount="indefinite"
            />
          </line>
          <line
            x1="118"
            y1="50"
            x2="272"
            y2="28"
            stroke="#00BFA5"
            strokeWidth="1.5"
            strokeDasharray="10 9"
            opacity="0.50"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="0;-19"
              dur="5s"
              repeatCount="indefinite"
            />
          </line>
          <line
            x1="272"
            y1="28"
            x2="350"
            y2="18"
            stroke="#00BFA5"
            strokeWidth="1.3"
            strokeDasharray="8 7"
            opacity="0.48"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="0;-15"
              dur="3.6s"
              repeatCount="indefinite"
            />
          </line>
          {/* Branch: Vijayawada → Guntur */}
          <line
            x1="118"
            y1="50"
            x2="150"
            y2="80"
            stroke="#4FC3F7"
            strokeWidth="1.1"
            strokeDasharray="6 8"
            opacity="0.40"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="14;0"
              dur="3.2s"
              repeatCount="indefinite"
            />
          </line>
          {/* Coastal shortcut: Guntur → Vizianagaram */}
          <line
            x1="150"
            y1="80"
            x2="272"
            y2="28"
            stroke="#80CBC4"
            strokeWidth="1"
            strokeDasharray="7 10"
            opacity="0.32"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="0;17"
              dur="6.5s"
              repeatCount="indefinite"
            />
          </line>
        </svg>

        {/* City dots — geographically positioned */}
        {[
          { lx: '7.4%', ty: 68, label: 'Hyderabad', color: '#4FC3F7', ping: true },
          { lx: '31%', ty: 50, label: 'Vijayawada', color: '#80CBC4', ping: true },
          { lx: '39.5%', ty: 80, label: 'Guntur', color: '#00BFA5', ping: false },
          { lx: '71.6%', ty: 28, label: 'Vizianagaram', color: '#80CBC4', ping: false },
          { lx: '92%', ty: 18, label: 'Vizag', color: '#00BFA5', ping: true },
        ].map((c, i) => (
          <div
            key={c.label}
            style={{
              position: 'absolute',
              left: c.lx,
              top: c.ty,
              transform: 'translate(-50%,-50%)',
              zIndex: 8,
            }}
          >
            {c.ping &&
              [0, 0.8].map((offset, ri) => (
                <div
                  key={ri}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 18 + ri * 12,
                    height: 18 + ri * 12,
                    borderRadius: '50%',
                    border: `1.1px solid ${c.color}`,
                    opacity: 0,
                    animation: `hl-ping 3s ease-out ${i * 0.6 + offset}s infinite`,
                    transform: 'translate(-50%,-50%)',
                  }}
                />
              ))}
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%,-50%)',
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: c.color,
                boxShadow: `0 0 6px ${c.color}66, 0 0 0 3px ${c.color}18`,
                zIndex: 2,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                fontSize: 8.5,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.58)',
                fontFamily: 'system-ui,sans-serif',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {c.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── Phone card — minimal, no orbits ── */}
      <div
        className="hl-phone-wrap"
        style={{
          position: 'absolute',
          right: '5%',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {/* Single soft pulsing ring around card */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 120,
              height: 182,
              borderRadius: 20,
              border: '1px solid rgba(0,191,165,0.28)',
              animation: 'hl-ring-pulse 3.2s ease-out 0s infinite',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 108,
              height: 170,
              borderRadius: 18,
              border: '1px solid rgba(0,191,165,0.18)',
              animation: 'hl-ring-pulse 3.2s ease-out 0.9s infinite',
            }}
          />

          {/* Card */}
          <div
            style={{
              background: 'rgba(8,22,20,0.88)',
              border: '1.5px solid rgba(0,191,165,0.52)',
              borderRadius: 18,
              padding: '10px 12px',
              boxShadow: '0 0 28px rgba(0,191,165,0.2), 0 6px 28px rgba(0,0,0,0.55)',
              backdropFilter: 'blur(18px)',
              animation: 'hl-glow-pulse 4s ease-in-out infinite',
              zIndex: 1,
            }}
          >
            <PhoneCard />
          </div>
        </div>

        {/* Label chip — clean and simple */}
        <div
          style={{
            background: 'rgba(0,191,165,0.14)',
            border: '1px solid rgba(0,191,165,0.42)',
            borderRadius: 18,
            padding: '5px 16px',
            fontSize: 10.5,
            fontWeight: 800,
            color: '#00BFA5',
            fontFamily: 'system-ui,sans-serif',
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            boxShadow: '0 0 12px rgba(0,191,165,0.16)',
          }}
        >
          Book a Pickup
        </div>
      </div>
    </div>
  );
}
